import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import {DisabledTimeConfig, DisabledTimeFn, DisabledTimePartial, NzDatePickerModule} from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTreeSelectModule } from 'ng-zorro-antd/tree-select';
import { NzTreeNodeOptions } from 'ng-zorro-antd/core/tree';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, takeUntil } from 'rxjs/operators';
import { AnnouncementAttachment, AnnouncementService, AnnouncementPayload } from '../services/announcement.service';
import { PropertyService } from '../services/property.service';
import { Router } from '@angular/router';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { shouldSuppressGenericError } from '../utils/http-error.utils';
import { AuthService } from '../services/auth.service';

interface AdminAnnouncement {
  _id: string;
  title: string;
  summary: string;
  urgency: 'normal' | 'urgent';
  validFrom?: string;
  validTo?: string;
  status?: string;
  scheduledFor?: string;
  sentAt?: string;
  updatedAt?: string;
  propertyId: string;
  propertyAddress?: string;
  property?: { _id: string; address: string };
  attachments?: AnnouncementAttachment[];
}

interface PropertyOption {
  id: string;
  label: string;
}

interface PersistResult {
  created?: AdminAnnouncement[];
  updated?: AdminAnnouncement;
}

interface AnnouncementHistoryEntry {
  action?: string;
  by?: string;
  at?: string | Date;
  notes?: string;
}

interface AdminAnnouncement {
  _id: string;
  title: string;
  summary: string;
  urgency: 'normal' | 'urgent';
  validFrom?: string;
  validTo?: string;
  status?: string;
  scheduledFor?: string;
  sentAt?: string;
  updatedAt?: string;
  propertyId: string;
  propertyAddress?: string;
  property?: { _id: string; address: string };
  attachments?: AnnouncementAttachment[];
}

interface PropertyOption {
  id: string;
  label: string;
}

interface PersistResult {
  created?: AdminAnnouncement[];
  updated?: AdminAnnouncement;
}

interface AdminAnnouncementRow extends AdminAnnouncement {
  /** All announcement document IDs that belong to this logical announcement */
  ids: string[];
  /** Number of properties this announcement is attached to (after filters) */
  propertyCount: number;
  /** Distinct property addresses for quick hints (first one shown in table) */
  propertyAddresses: string[];
}

interface AnnouncementAttachmentUi extends AnnouncementAttachment {
  clientId: string;
  uploadState?: 'persisted' | 'uploaded';
}

type AnnouncementAction = 'draft' | 'send' | 'schedule';

@Component({
  selector: 'app-announcements-admin',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzTableModule,
    NzButtonModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzDatePickerModule,
    NzModalModule,
    NzTagModule,
    NzTreeSelectModule,
    TranslateModule,
    NzAlertModule,
    NzCardModule,
    NzIconModule,
    NzToolTipModule,
    NzUploadModule,
    NzSpinModule,
  ],
  providers: [NzModalService, NzMessageService],
  templateUrl: './announcements.component.html',
  styleUrls: ['./announcements.component.less'],
})
export class AnnouncementsComponent implements OnInit, OnDestroy {
  private readonly historyActionLabelKeys: Record<string, string> = {
    created: 'ADMIN.ANNOUNCEMENTS.HISTORY_ACTIONS.CREATED',
    updated: 'ADMIN.ANNOUNCEMENTS.HISTORY_ACTIONS.UPDATED',
    scheduled: 'ADMIN.ANNOUNCEMENTS.HISTORY_ACTIONS.SCHEDULED',
    sent: 'ADMIN.ANNOUNCEMENTS.HISTORY_ACTIONS.SENT',
    expired: 'ADMIN.ANNOUNCEMENTS.HISTORY_ACTIONS.EXPIRED',
  };

  announcements: AdminAnnouncement[] = [];
  displayAnnouncements: AdminAnnouncementRow[] = [];

  loadingAnnouncements = false;
  loadingProperties = false;
  saving = false;
  deleting = false;
  scheduling = false;
  selectAllLoading = false;
  private formActionAttempted = false;

  selectedAnnouncementId: string | null = null;
  editingPropertyId: string | null = null;
  previousSelectionSnapshot: string[] | null = null;

  editingAnnouncementIds: string[] = [];

  selectedPropertyIds: string[] = [];
  selectAllCurrent = false;
  attachments: AnnouncementAttachmentUi[] = [];
  attachmentUploadingCount = 0;
  attachmentUploadingFileNames: string[] = [];

  form: FormGroup;

  tableSearch = '';
  urgencyFilter: 'all' | 'normal' | 'urgent' = 'all';
  statusFilter: 'all' | 'draft' | 'scheduled' | 'published' = 'all';

  pageIndex = 1;
  pageSize = 5;

  propertyNodes: NzTreeNodeOptions[] = [];
  private propertyOptionMap = new Map<string, string>();
  private propertySearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private suppressSelectionChange = false;
  currentFilterIds: string[] = [];
  private initialSelectAllDone = false;
  private initialAutoSelectPending = true;
  private attachmentClientIdSequence = 0;
  private allPropertyIds: string[] = [];
  private readonly maxAttachmentFileNameLength = 34;
  private allowedAttachmentExtensions = new Set(['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx']);

  private propertyQuery = {
    term: '',
    page: 1,
    pageSize: 50,
    hasMore: true,
  };
  notfallGuideUrl = '/assets/notfall-aushang/kurzanleitung-notfall-aushang.pdf';

  constructor(
    private fb: FormBuilder,
    private announcementService: AnnouncementService,
    private propertyService: PropertyService,
    private message: NzMessageService,
    private modal: NzModalService,
    private translate: TranslateService,
    private router: Router,
    private authService: AuthService,
  ) {
    this.form = this.fb.group(
      {
        propertyIds: [[], Validators.required],
        title: [null, [Validators.required, Validators.maxLength(120)]],
        summary: [null, [Validators.required, Validators.maxLength(500)]],
        urgency: ['normal', Validators.required],
        validFrom: [null, Validators.required],
        validTo: [null],
        scheduleAt: [null],
      },
      { validators: [this.validToAfterValidFromValidator, this.scheduleAtWithinValidityValidator] }
    );

  }

  get isEditing(): boolean {
    return !!this.selectedAnnouncementId;
  }

  get selectedPropertyCount(): number {
    return this.selectedPropertyIds.length;
  }

  // Disable all dates before today
  disabledPastDates = (current: Date): boolean => {
    if (!current) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const d = new Date(current);
    d.setHours(0, 0, 0, 0);

    return d < today;
  };

  disabledPastTime: DisabledTimeFn = (current: Date | Date[], partial?: DisabledTimePartial): DisabledTimeConfig | undefined => {
    // nz-date-picker usually passes a Date, but types allow Date[] (range picker)
    const date = Array.isArray(current) ? current[0] : current;

    if (!date) return undefined;

    const now = new Date();

    const sameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    // If not today → no disabled time
    if (!sameDay) return undefined;

    const disabledHours = Array.from({ length: now.getHours() }, (_, i) => i);

    return {
      nzDisabledHours: () => disabledHours,
      nzDisabledMinutes: (hour: number) => {
        if (hour !== now.getHours()) return [];
        return Array.from({ length: now.getMinutes() }, (_, i) => i);
      },
      nzDisabledSeconds: () => []
    };
  };

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  /**
   * ✅ Valid To must be AFTER Valid From (strictly >)
   * This is the safety net validator (even if UI is bypassed).
   */
  validToAfterValidFromValidator = (control: AbstractControl) => {
    const from: Date | null = control.get('validFrom')?.value ?? null;
    const to: Date | null = control.get('validTo')?.value ?? null;

    if (!from || !to) return null;

    return to.getTime() > from.getTime()
      ? null
      : { validToBeforeValidFrom: true };
  };

  /**
   * ✅ If scheduleAt is set, it must be:
   * - >= validFrom
   * - <= validTo (when validTo is set)
   * - and also >= now (future)
   */
  scheduleAtWithinValidityValidator = (control: AbstractControl) => {
    const from: Date | null = control.get('validFrom')?.value ?? null;
    const to: Date | null = control.get('validTo')?.value ?? null;
    const scheduleAt: Date | null = control.get('scheduleAt')?.value ?? null;

    if (!scheduleAt) return null; // only validate when user schedules

    const now = new Date();

    // must be in the future (or "now minute")
    if (scheduleAt.getTime() < now.getTime()) {
      return { scheduleAtInPast: true };
    }

    if (from && scheduleAt.getTime() < from.getTime()) {
      return { scheduleAtBeforeValidFrom: true };
    }

    if (to && scheduleAt.getTime() > to.getTime()) {
      return { scheduleAtAfterValidTo: true };
    }

    return null;
  };

  /**
   * ✅ Valid To date cannot be:
   * - in the past
   * - before the Valid From day
   */
  disabledValidToDates = (current: Date): boolean => {
    if (!current) return false;

    // Keep your rule: no past days
    if (this.disabledPastDates(current)) return true;

    const from: Date | null = this.form?.get('validFrom')?.value ?? null;
    if (!from) return false;

    // Disallow any day BEFORE the validFrom day
    return this.startOfDay(current) < this.startOfDay(from);
  };

  /**
   * ✅ Valid To time cannot be before/equal Valid From time if same day.
   * Also keeps your "disable past time for today" behavior by taking the max(now, validFrom).
   */
  disabledValidToTime: DisabledTimeFn = (current: Date | Date[], partial?: DisabledTimePartial): DisabledTimeConfig | undefined => {
    const selected = Array.isArray(current) ? current[0] : current;
    if (!selected) return undefined;

    const now = new Date();
    const from: Date | null = this.form?.get('validFrom')?.value ?? null;

    // Determine the minimum allowed datetime for "validTo" on this selected day:
    // - if selected day is today -> min is now
    // - if selected day is same as validFrom day -> min is validFrom
    // - if both apply -> min is max(now, validFrom)
    let min: Date | null = null;

    if (this.isSameDay(selected, now)) {
      min = now;
    }
    if (from && this.isSameDay(selected, from)) {
      min = min ? new Date(Math.max(min.getTime(), from.getTime())) : from;
    }

    // If no minimum for this day, no disabled time
    if (!min) return undefined;

    const minHour = min.getHours();
    const minMinute = min.getMinutes();

    // Strictly AFTER validFrom: disable minutes <= minMinute on the same hour.
    // (Seconds not shown in your picker, so this is enough.)
    return {
      nzDisabledHours: () => Array.from({ length: minHour }, (_, i) => i),
      nzDisabledMinutes: (hour: number) => {
        if (hour !== minHour) return [];
        return Array.from({ length: minMinute + 1 }, (_, i) => i); // +1 => strictly after
      },
      nzDisabledSeconds: () => []
    };
  };

  /**
   * ✅ ScheduleAt date:
   * - cannot be in the past
   * - cannot be before validFrom day
   * - cannot be after validTo day (if validTo exists)
   */
  disabledScheduleDates = (current: Date): boolean => {
    if (!current) return false;

    // rule: no past days
    if (this.disabledPastDates(current)) return true;

    const from: Date | null = this.form?.get('validFrom')?.value ?? null;
    const to: Date | null = this.form?.get('validTo')?.value ?? null;

    if (from && this.startOfDay(current) < this.startOfDay(from)) return true;
    if (to && this.startOfDay(current) > this.startOfDay(to)) return true;

    return false;
  };

  /**
   * ✅ ScheduleAt time:
   * - must be >= max(now, validFrom) when same day
   * - must be <= validTo when same day (if validTo exists)
   */
  disabledScheduleTime: DisabledTimeFn = (current: Date | Date[], partial?: DisabledTimePartial): DisabledTimeConfig | undefined => {
    const selected = Array.isArray(current) ? current[0] : current;
    if (!selected) return undefined;

    const now = new Date();
    const from: Date | null = this.form?.get('validFrom')?.value ?? null;
    const to: Date | null = this.form?.get('validTo')?.value ?? null;

    // min = max(now, validFrom) when applicable
    let min: Date | null = null;
    if (this.isSameDay(selected, now)) min = now;
    if (from && this.isSameDay(selected, from)) {
      min = min ? new Date(Math.max(min.getTime(), from.getTime())) : from;
    }

    // max = validTo when applicable
    const max: Date | null = (to && this.isSameDay(selected, to)) ? to : null;

    if (!min && !max) return undefined;

    const disabledHoursSet = new Set<number>();

    // disable hours before min hour
    if (min) {
      for (let h = 0; h < min.getHours(); h++) disabledHoursSet.add(h);
    }

    // disable hours after max hour
    if (max) {
      for (let h = max.getHours() + 1; h <= 23; h++) disabledHoursSet.add(h);
    }

    return {
      nzDisabledHours: () => Array.from(disabledHoursSet).sort((a, b) => a - b),

      nzDisabledMinutes: (hour: number) => {
        const mins = new Set<number>();

        // lower bound minutes on min hour (allow >= min minute)
        if (min && hour === min.getHours()) {
          for (let m = 0; m < min.getMinutes(); m++) mins.add(m);
        }

        // upper bound minutes on max hour (allow <= max minute)
        if (max && hour === max.getHours()) {
          for (let m = max.getMinutes() + 1; m <= 59; m++) mins.add(m);
        }

        return Array.from(mins).sort((a, b) => a - b);
      },

      nzDisabledSeconds: () => []
    };
  };

  ngOnInit(): void {
    this.propertySearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.resetPropertyQuery(term));

    this.resetPropertyQuery('');
    this.form
      .get('validFrom')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((from: Date | null) => {
        const to: Date | null = this.form.get('validTo')?.value ?? null;
        if (from && to && to.getTime() <= from.getTime()) {
          this.form.get('validTo')?.setValue(null);
        }
      });
    this.form
      .valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const from: Date | null = this.form.get('validFrom')?.value ?? null;
        const to: Date | null = this.form.get('validTo')?.value ?? null;
        const scheduleAt: Date | null = this.form.get('scheduleAt')?.value ?? null;

        if (!scheduleAt) return;

        const now = new Date();

        const invalid =
          scheduleAt.getTime() < now.getTime() ||
          (from && scheduleAt.getTime() < from.getTime()) ||
          (to && scheduleAt.getTime() > to.getTime());

        if (invalid) {
          this.form.get('scheduleAt')?.setValue(null, { emitEvent: false });
          // keep form validity updated
          this.form.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetPropertyQuery(term: string): void {
    this.propertyQuery = {
      ...this.propertyQuery,
      term,
      page: 1,
      hasMore: true,
    };
    this.currentFilterIds = [];
    this.selectAllCurrent = false;
    if (term) {
      this.initialAutoSelectPending = false;
    } else if (!this.initialSelectAllDone) {
      this.initialAutoSelectPending = true;
    }
    this.fetchPropertyPage(true);
  }

  selectAllInFilter(initial = false): void {
    if (this.selectAllLoading) {
      return;
    }
    this.selectAllLoading = true;
    this.propertyService
      .searchPropertyOptions(this.propertyQuery.term, 1, this.propertyQuery.pageSize, true)
      .pipe(finalize(() => (this.selectAllLoading = false)))
      .subscribe({
        next: (response) => {
          const items = response.items || [];
          this.mergePropertyOptions(items);
          this.currentFilterIds = items.map((item) => item.id);
          if (!this.propertyQuery.term) {
            this.allPropertyIds = [...this.currentFilterIds];
          }

          const union = new Set(this.selectedPropertyIds);
          this.currentFilterIds.forEach((id) => union.add(id));
          this.updateSelection(Array.from(union), true);
          this.updateSelectAllFlag();
          if (initial) {
            this.initialSelectAllDone = true;
          } else if (this.currentFilterIds.length) {
            this.message.success(
              this.translate.instant('ADMIN.ANNOUNCEMENTS.PROPERTY_TOOLBAR.SELECT_ALL_TOAST', {
                count: this.currentFilterIds.length,
              }),
            );
          }
        },
        error: () => {
          this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_PROPERTIES'));
        },
      });
  }

  clearPropertySelection(): void {
    this.updateSelection([], true);
  }

  onPropertySelectionChange(value: string[]): void {
    if (this.suppressSelectionChange) {
      return;
    }
    this.updateSelection(value || [], true);
  }

  private updateSelection(ids: string[], triggerLoad: boolean): void {
    const unique = Array.from(new Set((ids || []).filter(Boolean)));
    this.suppressSelectionChange = true;
    this.selectedPropertyIds = unique;
    this.form.patchValue({ propertyIds: unique }, { emitEvent: false });
    this.suppressSelectionChange = false;
    this.updateSelectAllFlag();
    if (triggerLoad) {
      this.loadAnnouncements();
    }
  }

  private updateSelectAllFlag(): void {
    if (!this.currentFilterIds.length) {
      this.selectAllCurrent = false;
      return;
    }
    const set = new Set(this.selectedPropertyIds);
    this.selectAllCurrent = this.currentFilterIds.every((id) => set.has(id));
  }

  private fetchPropertyPage(reset = false): void {
    if (this.loadingProperties) {
      return;
    }
    if (!reset && !this.propertyQuery.hasMore) {
      return;
    }

    const pageToLoad = reset ? 1 : this.propertyQuery.page;
    if (reset) {
      this.propertyQuery.hasMore = true;
      this.currentFilterIds = [];
    }

    this.loadingProperties = true;
    this.propertyService
      .searchPropertyOptions(this.propertyQuery.term, pageToLoad, this.propertyQuery.pageSize)
      .pipe(finalize(() => (this.loadingProperties = false)))
      .subscribe({
        next: (response) => {
          const items = response.items || [];
          if (pageToLoad === 1) {
            this.currentFilterIds = [];
          }
          this.mergePropertyOptions(items);
          this.currentFilterIds = Array.from(
            new Set([...this.currentFilterIds, ...items.map((item) => item.id)])
          );

          this.propertyQuery.hasMore = response.hasMore;
          this.propertyQuery.page = response.hasMore ? pageToLoad + 1 : pageToLoad;

          if (this.initialAutoSelectPending && !this.propertyQuery.term) {
            this.initialAutoSelectPending = false;
            this.selectAllInFilter(true);
          } else {
            this.updateSelectAllFlag();
          }
        },
        error: () => {
          this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_PROPERTIES'));
        },
      });
  }

  private mergePropertyOptions(options: PropertyOption[]): void {
    let changed = false;
    options.forEach((option) => {
      if (!option.id) {
        return;
      }
      const existing = this.propertyOptionMap.get(option.id);
      if (!existing || (option.label && existing !== option.label)) {
        this.propertyOptionMap.set(option.id, option.label || option.id);
        changed = true;
      }
    });
    if (changed) {
      this.refreshPropertyNodes();
    }
  }

  private refreshPropertyNodes(): void {
    const nodes = Array.from(this.propertyOptionMap.entries())
      .map<NzTreeNodeOptions>(([key, title]) => ({ key, title, isLeaf: true }))
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
    this.propertyNodes = nodes;
  }

  private ensurePropertyOption(id: string, label?: string): void {
    if (!id) {
      return;
    }
    const existing = this.propertyOptionMap.get(id);
    const desired = label || existing || id;
    if (existing !== desired) {
      this.propertyOptionMap.set(id, desired);
      this.refreshPropertyNodes();
    }
  }

  loadAnnouncements(): void {
    if (!this.selectedPropertyIds.length) {
      this.announcements = [];
      this.displayAnnouncements = [];
      return;
    }
    this.loadingAnnouncements = true;
    this.announcementService.list(this.selectedPropertyIds).subscribe({
      next: (data) => {
        const rows = Array.isArray(data)
          ? data.map((item) => this.normalizeAnnouncement(item))
          : [];
        this.announcements = rows;
        this.applyTableFilters();
        this.loadingAnnouncements = false;
      },
      error: () => {
        this.loadingAnnouncements = false;
        this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_LOAD'));
      },
    });
  }

  private normalizeAnnouncement(data: any): AdminAnnouncement {
    if (!data) {
      throw new Error('Invalid announcement payload');
    }
    const propertyId = data.propertyId?._id
      ? data.propertyId._id.toString()
      : data.propertyId?.toString?.() || data.propertyId;
    const propertyAddress = data.propertyAddress || data.property?.address || '';

    if (propertyId) {
      this.ensurePropertyOption(propertyId, propertyAddress || undefined);
    }

    return {
      ...data,
      _id: data._id?.toString?.() || data._id,
      propertyId,
      propertyAddress,
      property: propertyAddress
        ? { _id: propertyId, address: propertyAddress }
        : data.property,
      attachments: this.normalizeAttachments(data.attachments || [], 'persisted'),
    } as AdminAnnouncement;
  }

  private createAttachmentClientId(): string {
    this.attachmentClientIdSequence += 1;
    return `announcement-attachment-${this.attachmentClientIdSequence}`;
  }

  private normalizeAttachments(
    files: AnnouncementAttachment[] = [],
    uploadState: AnnouncementAttachmentUi['uploadState'] = 'uploaded'
  ): AnnouncementAttachmentUi[] {
    return (Array.isArray(files) ? files : []).map((file) => ({
      ...file,
      clientId: file.clientId || this.createAttachmentClientId(),
      uploadState,
    }));
  }

  private toSavableAttachments(): AnnouncementAttachment[] {
    return this.attachments.map((file) => ({
      fileName: file.fileName,
      fileType: file.fileType,
      url: file.url,
      uploadedAt: file.uploadedAt,
      s3Key: file.s3Key,
      sourceS3Key: file.sourceS3Key,
    }));
  }

  editAnnouncement(item: AdminAnnouncementRow): void {
    this.formActionAttempted = false;
    // keep reference to a "primary" ID/property (for history etc.)
    this.selectedAnnouncementId = item._id;
    this.editingPropertyId = item.propertyId;
    this.previousSelectionSnapshot = [...this.selectedPropertyIds];

    // 👉 all real announcement documents behind this row
    this.editingAnnouncementIds =
      item.ids && item.ids.length
        ? [...item.ids]
        : (item._id ? [item._id] : []);

    this.ensurePropertyOption(item.propertyId, item.propertyAddress);
    this.setScheduleAtRequired(false);

    this.form.patchValue({
      propertyIds: this.selectedPropertyIds, // hidden control, keeps context
      title: item.title,
      summary: item.summary,
      urgency: item.urgency || 'normal',
      validFrom: item.validFrom ? new Date(item.validFrom) : null,
      validTo: item.validTo ? new Date(item.validTo) : null,
      scheduleAt: item.scheduledFor ? new Date(item.scheduledFor) : null,
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.attachments = this.normalizeAttachments(item.attachments || [], 'persisted');
  }

  clearSelection(): void {
    this.formActionAttempted = false;
    const snapshot = this.previousSelectionSnapshot ? [...this.previousSelectionSnapshot] : null;
    this.previousSelectionSnapshot = null;
    if (snapshot) {
      this.updateSelection(snapshot, false);
    }

    this.selectedAnnouncementId = null;
    this.editingPropertyId = null;
    this.editingAnnouncementIds = [];
    this.attachments = [];
    this.attachmentUploadingCount = 0;
    this.attachmentUploadingFileNames = [];
    this.setScheduleAtRequired(false);

    this.form.reset({
      propertyIds: this.selectedPropertyIds,
      title: null,
      summary: null,
      urgency: 'normal',
      validFrom: null,
      validTo: null,
      scheduleAt: null,
    });

    if (this.selectedPropertyIds.length) {
      this.loadAnnouncements();
    } else {
      this.announcements = [];
      this.displayAnnouncements = [];
    }
  }

  private buildPayload(): AnnouncementPayload {
    const raw = this.form.value;
    const payload: AnnouncementPayload = {
      title: raw.title,
      summary: raw.summary,
      urgency: raw.urgency,
      validFrom: raw.validFrom ? raw.validFrom.toISOString() : null,
      validTo: raw.validTo ? raw.validTo.toISOString() : null,
      attachments: this.toSavableAttachments(),
    } as AnnouncementPayload;

    // For NEW announcements (not editing existing ones),
    // include target propertyIds so BE can create one per property.
    if (!this.editingAnnouncementIds.length) {
      payload.propertyIds = [...this.selectedPropertyIds];
    }

    return payload;
  }

  private persistCurrentForm(options: { failOnAnyError?: boolean } = {}): Observable<PersistResult> | null {
    const { failOnAnyError = false } = options;
    const isEditingExisting = this.editingAnnouncementIds.length > 0;

    // For new announcements we still require at least one property selected
    if (!isEditingExisting && !this.selectedPropertyIds.length) {
      this.markFormValidationAttempt();
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.REQUIRE_PROPERTY'));
      return null;
    }

    if (this.form.invalid) {
      this.markFormValidationAttempt();
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_FORM'));
      return null;
    }

    if (this.hasAttachmentUploadsInProgress) {
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.UPLOADS_IN_PROGRESS_BLOCKED'));
      return null;
    }

    const payload = this.buildPayload();
    let request$: Observable<PersistResult>;

    if (isEditingExisting) {
      // 👉 bulk update all announcement docs in this logical group
      const ids = [...this.editingAnnouncementIds];
      const tasks = ids.map((id) =>
        this.announcementService
          .update(id, payload)
          .pipe(
            map((res) => this.normalizeAnnouncement(res)),
            catchError((error) => of({ __error: error }))
          )
      );

      request$ = forkJoin(tasks).pipe(
        map((items: any[]) => {
          const failures = items.filter((item) => item && item.__error).map((item) => item.__error);
          if (failOnAnyError && failures.length > 0) {
            throw failures[0];
          }
          const successes = items.filter((item) => item && !item.__error) as AdminAnnouncement[];
          const updated = successes[0] || undefined;
          return { updated };
        })
      );
    } else {
      // 👉 create new logical announcement = one document per selected property
      const create$ = this.announcementService.create(payload);
      request$ = create$.pipe(map((response) => this.mapPersistResponse(response)));
    }

    this.saving = true;
    return request$.pipe(
      finalize(() => {
        this.saving = false;
      })
    );
  }

  private mapPersistResponse(response: any): PersistResult {
    if (!response) {
      return {};
    }
    if (Array.isArray(response)) {
      return { created: response.map((item) => this.normalizeAnnouncement(item)) };
    }
    if (Array.isArray(response.items)) {
      return { created: response.items.map((item: any) => this.normalizeAnnouncement(item)) };
    }
    if (response._id) {
      return { updated: this.normalizeAnnouncement(response) };
    }
    return {};
  }

  private extractAnnouncementIds(result: PersistResult): string[] {
    // 👉 in edit mode, we always want to operate on ALL documents in the group
    if (this.editingAnnouncementIds.length) {
      return [...this.editingAnnouncementIds];
    }
    if (result.created?.length) {
      return result.created.map((item) => item._id).filter(Boolean);
    }
    if (result.updated?._id) {
      return [result.updated._id];
    }
    return [];
  }

  private isAllPropertiesSelected(): boolean {
    const allIds = this.allPropertyIds.length
      ? this.allPropertyIds
      : (!this.propertyQuery.term ? this.currentFilterIds : []);

    if (!allIds.length) {
      return false;
    }

    const selected = new Set(this.selectedPropertyIds);
    return allIds.every((id) => selected.has(id));
  }

  private validateAnnouncementAction(action: AnnouncementAction): boolean {
    const isEditingExisting = this.editingAnnouncementIds.length > 0;
    this.setScheduleAtRequired(action === 'schedule');

    if (!isEditingExisting && !this.selectedPropertyIds.length) {
      this.markFormValidationAttempt();
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.REQUIRE_PROPERTY'));
      return false;
    }

    if (action === 'schedule' && !this.form.value.scheduleAt) {
      this.markFormValidationAttempt();
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.REQUIRE_SCHEDULE'));
      return false;
    }

    if (this.form.invalid) {
      this.markFormValidationAttempt();
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_FORM'));
      return false;
    }

    if (this.hasAttachmentUploadsInProgress) {
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.UPLOADS_IN_PROGRESS_BLOCKED'));
      return false;
    }

    if (this.attachments.some((file) => !this.isAttachmentFileNameValid(file.fileName))) {
      this.message.warning(
        this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.ERROR_FILENAME_TOO_LONG', {
          max: this.maxAttachmentFileNameLength,
        })
      );
      return false;
    }

    return true;
  }

  private setScheduleAtRequired(required: boolean): void {
    const scheduleAtControl = this.form.get('scheduleAt');
    if (!scheduleAtControl) {
      return;
    }

    if (required) {
      scheduleAtControl.addValidators(Validators.required);
    } else {
      scheduleAtControl.removeValidators(Validators.required);
    }
    scheduleAtControl.updateValueAndValidity({ emitEvent: false });
  }

  private markFormValidationAttempt(): void {
    this.formActionAttempted = true;
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  hasControlError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (this.formActionAttempted || control.touched);
  }

  controlValidationStatus(controlName: string): 'error' | '' {
    return this.hasControlError(controlName) ? 'error' : '';
  }

  private confirmAllPropertiesIfNeeded(action: AnnouncementAction, onConfirm: () => void): void {
    if (this.editingAnnouncementIds.length || !this.isAllPropertiesSelected()) {
      onConfirm();
      return;
    }

    this.modal.confirm({
      nzTitle: this.translate.instant('ADMIN.ANNOUNCEMENTS.ALL_PROPERTIES_CONFIRM.TITLE'),
      nzContent: this.translate.instant(`ADMIN.ANNOUNCEMENTS.ALL_PROPERTIES_CONFIRM.${action.toUpperCase()}`, {
        count: this.selectedPropertyIds.length,
      }),
      nzOkText: this.translate.instant('ADMIN.ANNOUNCEMENTS.ALL_PROPERTIES_CONFIRM.OK'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: onConfirm,
    });
  }

  private runBulkOperation(ids: string[], mode: 'send' | 'schedule', scheduleIso?: string): void {
    if (!ids.length) {
      return;
    }
    const tasks = ids.map((id) => {
      const request = mode === 'send'
        ? this.announcementService.sendNow(id)
        : this.announcementService.schedule(id, scheduleIso!);
      return request.pipe(
        map(() => ({ id, success: true })),
        catchError((error) => of({ id, success: false, error }))
      );
    });

    this.scheduling = true;
    forkJoin(tasks)
      .pipe(finalize(() => {
        this.scheduling = false;
      }))
      .subscribe({
        next: (results) => {
          const failures = results.filter((result: any) => !result.success);
          const permissionOnlyFailures = failures.length > 0
            && failures.every((result: any) => shouldSuppressGenericError(result.error));
          if (failures.length && !permissionOnlyFailures) {
            const key = mode === 'send'
              ? 'ADMIN.ANNOUNCEMENTS.ERROR_PARTIAL_SEND'
              : 'ADMIN.ANNOUNCEMENTS.ERROR_PARTIAL_SCHEDULE';
            this.message.warning(
              this.translate.instant(key, {
                failed: failures.length,
                total: ids.length,
              })
            );
          } else if (failures.length === 0) {
            const successKey = mode === 'send'
              ? (ids.length > 1
                ? 'ADMIN.ANNOUNCEMENTS.SENT_MULTIPLE'
                : 'ADMIN.ANNOUNCEMENTS.SENT')
              : (ids.length > 1
                ? 'ADMIN.ANNOUNCEMENTS.SCHEDULED_MULTIPLE'
                : 'ADMIN.ANNOUNCEMENTS.SCHEDULED');
            this.message.success(this.translate.instant(successKey, { count: ids.length }));
          }
          this.clearSelection();
        },
        error: (error) => {
          if (shouldSuppressGenericError(error)) {
            return;
          }
          const key = mode === 'send'
            ? 'ADMIN.ANNOUNCEMENTS.ERROR_SEND'
            : 'ADMIN.ANNOUNCEMENTS.ERROR_SCHEDULE';
          this.message.error(this.translate.instant(key));
        },
      });
  }

  saveDraft(): void {
    if (this.saving || this.scheduling || !this.canWriteAnnouncements) {
      return;
    }

    if (!this.validateAnnouncementAction('draft')) {
      return;
    }

    this.confirmAllPropertiesIfNeeded('draft', () => this.performSaveDraft());
  }

  private performSaveDraft(): void {
    const isEditingExisting = this.editingAnnouncementIds.length > 0;
    const request = this.persistCurrentForm();
    if (!request) {
      return;
    }

    request.subscribe({
      next: (result) => {
        if (isEditingExisting) {
          const count = this.editingAnnouncementIds.length || 1;
          const key =
            count > 1
              ? 'ADMIN.ANNOUNCEMENTS.UPDATED_MULTIPLE'
              : 'ADMIN.ANNOUNCEMENTS.UPDATED';
          this.message.success(this.translate.instant(key, { count }));
        } else {
          const createdCount = result.created?.length ?? 0;
          const key =
            createdCount > 1
              ? 'ADMIN.ANNOUNCEMENTS.SAVED_MULTIPLE'
              : 'ADMIN.ANNOUNCEMENTS.SAVED';
          this.message.success(
            this.translate.instant(key, { count: Math.max(createdCount, 1) })
          );
        }
        this.clearSelection();
      },
      error: (error) => {
        if (!shouldSuppressGenericError(error)) {
          this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_SAVE'));
        }
      },
    });
  }

  sendNow(): void {
    if (this.saving || this.scheduling || !this.canSendAnnouncements) {
      return;
    }

    if (!this.validateAnnouncementAction('send')) {
      return;
    }

    this.confirmAllPropertiesIfNeeded('send', () => this.performSendNow());
  }

  private performSendNow(): void {
    const request = this.persistCurrentForm({ failOnAnyError: true });
    if (!request) {
      return;
    }
    request.subscribe({
      next: (result) => {
        const ids = this.extractAnnouncementIds(result); // 👉 returns full group when editing
        this.runBulkOperation(ids, 'send');
      },
      error: (error) => {
        if (!shouldSuppressGenericError(error)) {
          this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_SAVE'));
        }
      },
    });
  }

  scheduleSend(): void {
    if (this.saving || this.scheduling || !this.canSendAnnouncements) {
      return;
    }

    if (!this.validateAnnouncementAction('schedule')) {
      return;
    }

    this.confirmAllPropertiesIfNeeded('schedule', () => this.performScheduleSend());
  }

  private performScheduleSend(): void {
    const scheduleAt: Date | null = this.form.value.scheduleAt;
    if (!scheduleAt) {
      return;
    }
    const request = this.persistCurrentForm({ failOnAnyError: true });
    if (!request) {
      return;
    }
    request.subscribe({
      next: (result) => {
        const ids = this.extractAnnouncementIds(result); // 👉 group IDs here
        this.runBulkOperation(ids, 'schedule', scheduleAt.toISOString());
      },
      error: (error) => {
        if (!shouldSuppressGenericError(error)) {
          this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_SAVE'));
        }
      },
    });
  }

  showHistory(announcement: AdminAnnouncement): void {
    this.announcementService.history(announcement._id).subscribe({
      next: (history) => {
        const currentLang = this.translate.currentLang || this.translate.defaultLang || 'en';
        const historyMarkup = ((history || []) as AnnouncementHistoryEntry[])
          .map((entry) => {
            const timestamp = this.escapeHistoryHtml(formatDate(entry.at || new Date(), 'short', currentLang));
            const action = this.escapeHistoryHtml(this.getHistoryActionLabel(entry.action));
            return `<li><strong>${timestamp}</strong> - ${action}</li>`;
          })
          .join('');
        this.modal.create({
          nzTitle: this.translate.instant('ADMIN.ANNOUNCEMENTS.HISTORY_TITLE'),
          nzContent: `<ul class="history-list">${historyMarkup}</ul>`,
          nzClosable: true,
          nzOkText: this.translate.instant('COMMON.GOT_IT'),
        });
      },
      error: () => {
        this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_HISTORY'));
      },
    });
  }

  private getHistoryActionLabel(action: string | undefined): string {
    const normalizedAction = String(action || '').trim().toLowerCase();
    const labelKey = this.historyActionLabelKeys[normalizedAction]
      || 'ADMIN.ANNOUNCEMENTS.HISTORY_ACTIONS.UNKNOWN';
    return this.translate.instant(labelKey);
  }

  private escapeHistoryHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  confirmDelete(item: AdminAnnouncementRow): void {
    if (!this.canDeleteAnnouncements) return;
    if (!item) return;

    const ids = item.ids && item.ids.length ? [...item.ids] : (item._id ? [item._id] : []);
    if (!ids.length) return;

    this.modal.confirm({
      nzTitle: this.translate.instant('ADMIN.ANNOUNCEMENTS.DELETE_CONFIRM_TITLE'),
      nzContent: this.translate.instant('ADMIN.ANNOUNCEMENTS.DELETE_CONFIRM_CONTENT', {
        title: item.title || '',
        count: ids.length,
      }),
      nzOkText: this.translate.instant('ADMIN.ANNOUNCEMENTS.DELETE_CONFIRM_OK'),
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOkDanger: true,
      nzOnOk: () => this.deleteAnnouncements(ids),
    });
  }

  private deleteAnnouncements(ids: string[]): void {
    if (!this.canDeleteAnnouncements) return;
    if (!ids.length || this.deleting) return;

    this.deleting = true;

    forkJoin(
      ids.map((id) =>
        this.announcementService.delete(id).pipe(
          map(() => ({ id, success: true })),
          catchError((error) => of({ id, success: false, error }))
        )
      )
    )
      .pipe(finalize(() => (this.deleting = false)))
      .subscribe({
        next: (results) => {
          const failures = results.filter((r: any) => !r.success);
          const permissionOnlyFailures = failures.length > 0
            && failures.every((r: any) => shouldSuppressGenericError(r.error));

          if (failures.length && !permissionOnlyFailures) {
            this.message.warning(
              this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_PARTIAL_DELETE', {
                failed: failures.length,
                total: ids.length,
              })
            );
          } else if (failures.length === 0) {
            const key =
              ids.length > 1
                ? 'ADMIN.ANNOUNCEMENTS.DELETED_MULTIPLE'
                : 'ADMIN.ANNOUNCEMENTS.DELETED';

            this.message.success(this.translate.instant(key, { count: ids.length }));
          }

          // If user was editing something that got deleted, reset form state
          if (this.selectedAnnouncementId && ids.includes(this.selectedAnnouncementId)) {
            this.clearSelection();
          } else {
            // refresh list
            this.loadAnnouncements();
          }
        },
        error: (error) => {
          if (!shouldSuppressGenericError(error)) {
            this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ERROR_DELETE'));
          }
        },
      });
  }

  get hasAttachmentUploadsInProgress(): boolean {
    return this.attachmentUploadingCount > 0;
  }

  uploadAttachmentRequest = (option: any): Subscription => {
    const rawFile = option.file as File;
    const fileName = rawFile?.name || '';
    const extension = this.getFileExtension(rawFile?.name);

    if (!this.isAttachmentFileNameValid(fileName)) {
      this.message.error(
        this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.ERROR_FILENAME_TOO_LONG', {
          max: this.maxAttachmentFileNameLength,
        })
      );
      option.onError?.(new Error('File name too long'));
      return new Subscription();
    }

    if (!extension || !this.allowedAttachmentExtensions.has(extension)) {
      const errorText = this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.ERROR_UNSUPPORTED_FILE_TYPE');
      const hintText = this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.SUPPORTED_TYPES_HINT');
      this.message.error(`${errorText} ${hintText}`.trim());
      option.onError?.(new Error('Unsupported file type'));
      return new Subscription();
    }

    this.markAttachmentUploadStarted(rawFile?.name);

    return this.announcementService.uploadAttachment(rawFile)
      .pipe(finalize(() => this.markAttachmentUploadFinished(rawFile?.name)))
      .subscribe({
        next: (file) => {
          this.attachments = [
            ...this.attachments,
            ...this.normalizeAttachments([file], 'uploaded'),
          ];
          option.onSuccess?.({}, rawFile);
        },
        error: () => {
          this.message.error(this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.ERROR_UPLOAD'));
          option.onError?.(new Error('Upload failed'));
        },
      });
  };

  removeAttachment(file: AnnouncementAttachmentUi): void {
    if (this.hasAttachmentUploadsInProgress) {
      this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.UPLOADS_IN_PROGRESS_BLOCKED'));
      return;
    }

    this.attachments = this.attachments.filter((item) => item.clientId !== file.clientId);

    if (file.uploadState === 'uploaded' && file.s3Key) {
      this.announcementService.deleteAttachment(file.s3Key).subscribe({
        error: () => {
          this.message.warning(this.translate.instant('ADMIN.ANNOUNCEMENTS.ATTACHMENTS.ERROR_REMOVE_REMOTE'));
        },
      });
    }
  }

  trackByAttachment = (_index: number, file: AnnouncementAttachmentUi): string =>
    file.clientId || file.s3Key || file.url || file.fileName;

  private markAttachmentUploadStarted(fileName?: string): void {
    this.attachmentUploadingCount += 1;
    this.attachmentUploadingFileNames = fileName
      ? [...this.attachmentUploadingFileNames, fileName]
      : [...this.attachmentUploadingFileNames];
  }

  private markAttachmentUploadFinished(fileName?: string): void {
    this.attachmentUploadingCount = Math.max(this.attachmentUploadingCount - 1, 0);
    if (fileName) {
      const names = [...this.attachmentUploadingFileNames];
      const index = names.indexOf(fileName);
      if (index >= 0) {
        names.splice(index, 1);
      }
      this.attachmentUploadingFileNames = names;
    }
  }

  private getFileExtension(fileName?: string): string {
    const name = (fileName || '').trim();
    const dotIndex = name.lastIndexOf('.');
    return dotIndex >= 0 ? name.slice(dotIndex + 1).toLowerCase() : '';
  }

  private isAttachmentFileNameValid(fileName?: string): boolean {
    return (fileName || '').trim().length <= this.maxAttachmentFileNameLength;
  }

  applyTableFilters(): void {
    // 1) Start from all announcements loaded for the selected properties
    let rows: AdminAnnouncement[] = [...this.announcements];

    // 2) Apply urgency filter
    if (this.urgencyFilter !== 'all') {
      rows = rows.filter((r) => (r.urgency || 'normal') === this.urgencyFilter);
    }

    // 3) Apply status filter
    if (this.statusFilter !== 'all') {
      rows = rows.filter((r) => (r.status || 'draft') === this.statusFilter);
    }

    // 4) Apply text search (title, summary, property address)
    const q = (this.tableSearch || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        (r.title || '').toLowerCase().includes(q) ||
        (r.summary || '').toLowerCase().includes(q) ||
        (r.propertyAddress || '').toLowerCase().includes(q)
      );
    }

    // 5) Group rows by their "template" (content + validity + status)
    const grouped = new Map<string, AdminAnnouncementRow>();

    for (const row of rows) {
      const key = this.buildGroupKey(row);
      const existing = grouped.get(key);

      if (!existing) {
        // First time we see this template -> create a group row
        grouped.set(key, {
          ...row,
          ids: row._id ? [row._id] : [],
          propertyCount: 1,
          propertyAddresses: row.propertyAddress ? [row.propertyAddress] : [],
        });
      } else {
        // Merge into existing group
        if (row._id) {
          existing.ids.push(row._id);
        }
        existing.propertyCount += 1;

        if (
          row.propertyAddress &&
          !existing.propertyAddresses.includes(row.propertyAddress)
        ) {
          existing.propertyAddresses.push(row.propertyAddress);
        }
      }
    }

    // 6) Flatten groups into display array
    this.displayAnnouncements = Array.from(grouped.values());

    // 7) Fix pagination if current page index is now out of bounds
    const maxIndex = Math.max(
      1,
      Math.ceil(this.displayAnnouncements.length / this.pageSize)
    );
    if (this.pageIndex > maxIndex) {
      this.pageIndex = 1;
    }
  }

  sortByTitle = (a: AdminAnnouncementRow, b: AdminAnnouncementRow) =>
    (a.title || '').localeCompare(b.title || '');

  sortByProperty = (a: AdminAnnouncementRow, b: AdminAnnouncementRow) =>
    (a.propertyAddress || '').localeCompare(b.propertyAddress || '', undefined, { sensitivity: 'base' });

  sortByUrgency = (a: AdminAnnouncementRow, b: AdminAnnouncementRow) => {
    const order = (u: string | undefined) => (u === 'urgent' ? 0 : 1);
    return order(a.urgency) - order(b.urgency);
  };

  sortByStatus = (a: AdminAnnouncementRow, b: AdminAnnouncementRow) => {
    const rank = (s?: string) => ({ draft: 0, scheduled: 1, published: 2 }[s || 'draft'] ?? 0);
    return rank(a.status) - rank(b.status);
  };

  sortByValidity = (a: AdminAnnouncementRow, b: AdminAnnouncementRow) => {
    const ad = a.validFrom ? new Date(a.validFrom).getTime() : 0;
    const bd = b.validFrom ? new Date(b.validFrom).getTime() : 0;
    return ad - bd;
  };

  sortByScheduled = (a: AdminAnnouncementRow, b: AdminAnnouncementRow) => {
    const get = (x: AdminAnnouncementRow) =>
      x.scheduledFor ? new Date(x.scheduledFor).getTime()
        : x.sentAt ? new Date(x.sentAt).getTime()
          : 0;
    return get(a) - get(b);
  };

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    const maxIndex = Math.max(1, Math.ceil(this.displayAnnouncements.length / this.pageSize));
    if (this.pageIndex > maxIndex) {
      this.pageIndex = 1;
    }
  }

  /**
   * Build a grouping key so that announcements with identical "template"
   * (title, summary, urgency, validity, status, schedule) are shown as ONE row.
   */
  private buildGroupKey(row: AdminAnnouncement): string {
    return [
      row.title || '',
      row.summary || '',
      row.urgency || 'normal',
      row.validFrom ? new Date(row.validFrom).toISOString() : '',
      row.validTo ? new Date(row.validTo).toISOString() : '',
      row.status || 'draft',
      row.scheduledFor ? new Date(row.scheduledFor).toISOString() : '',
      JSON.stringify((row.attachments || []).map((file) => file.s3Key || file.url || file.fileName).sort()),
    ].join('||');
  }

  get isBulkEditing(): boolean {
    return this.editingAnnouncementIds.length > 1;
  }

  goToProperties(): void {
    this.router.navigateByUrl('/properties');
  }

  get propertyVirtualHeight(): string {
    // When there are no properties, don't reserve a 240px virtual list
    return this.propertyNodes?.length ? '240px' : '24px';
  }

  get canWriteAnnouncements(): boolean {
    return this.authService.hasPermission('company.announcements.write');
  }

  get canSendAnnouncements(): boolean {
    return this.authService.hasPermission('company.announcements.send');
  }

  get canDeleteAnnouncements(): boolean {
    return this.authService.hasPermission('company.announcements.delete');
  }
}
